/**
 * templateDrag.ts
 * Free-form drag-and-drop of layout blocks inside the rendered template preview,
 * powered by interact.js. interact.js attaches directly to the existing DOM nodes
 * that docx-preview produced (paragraphs, tables, divs) — no React wrapper needed —
 * and handles pointer/mouse/touch capture while preventing text selection during a
 * real drag. A plain click still falls through to text editing (contentEditable).
 *
 * The resulting translate is applied as `transform` on the element and later picked
 * up by `captureTemplateLayoutEdits` so it persists into the final output.
 */

import interact from "interactjs";

type Interactable = ReturnType<typeof interact>;

/** Wrapper/header/footer tags and classes generated by docx-preview that are layout wrappers, not draggable content blocks. */
const WRAPPER_TAGS = new Set(["SECTION", "ARTICLE", "MAIN", "BODY"]);
const WRAPPER_CLASSES = [
  "docx-wrapper",
  "docx-container",
  "docx-bg",
  "docx-pb",
  "docx-page-footer",
  "docx-section-marker",
  "docx",
];

function isWrapper(el: HTMLElement): boolean {
  if (WRAPPER_TAGS.has(el.tagName)) return true;
  if (WRAPPER_CLASSES.some((cls) => el.classList && el.classList.contains(cls))) return true;
  return false;
}

/**
 * Checks if `el` is nested inside another content block (e.g., a paragraph inside a table cell,
 * or a table cell inside a table). We only want to drag top-level content blocks (paragraphs, tables).
 */
function isNestedInContentBlock(el: HTMLElement, container: HTMLElement): boolean {
  let parent = el.parentElement;
  while (parent && parent !== container) {
    if (
      parent.tagName === "P" ||
      parent.tagName === "TABLE" ||
      parent.tagName === "TD" ||
      parent.tagName === "TH" ||
      parent.tagName === "TR" ||
      parent.tagName === "TBODY" ||
      parent.tagName === "THEAD" ||
      parent.tagName === "TFOOT"
    ) {
      return true;
    }
    parent = parent.parentElement;
  }
  return false;
}

function collectTargets(container: HTMLElement): HTMLElement[] {
  const sections = Array.from(container.querySelectorAll<HTMLElement>("section.docx"));
  const roots = sections.length > 0 ? sections : [container];
  const out: HTMLElement[] = [];
  roots.forEach((sec) => {
    sec.querySelectorAll<HTMLElement>("p, table, h1, h2, h3, h4, div").forEach((el) => {
      if (isWrapper(el)) return;
      if (isNestedInContentBlock(el, container)) return;

      // For DIVs: only collect if it's a leaf block (doesn't contain nested paragraphs or tables)
      if (el.tagName === "DIV") {
        const hasBlockChildren = el.querySelector("p, table, h1, h2, h3, h4, div") !== null;
        if (hasBlockChildren) return;
      }

      out.push(el);
    });
  });
  return out;
}

const positions = new WeakMap<HTMLElement, { x: number; y: number }>();
const interactables = new WeakMap<HTMLElement, Interactable>();

export function getElementPosition(el: HTMLElement): { x: number; y: number } {
  const p = positions.get(el);
  if (p) return { ...p };
  const existing = /translate\(\s*(-?[0-9.]+)px\s*,\s*(-?[0-9.]+)px\s*\)/.exec(
    el.style.transform || ""
  );
  if (existing) {
    return { x: parseFloat(existing[1]), y: parseFloat(existing[2]) };
  }
  return { x: 0, y: 0 };
}

export function nudgeElementPosition(
  el: HTMLElement,
  dx: number,
  dy: number,
  onChanged?: () => void
): void {
  if (!el) return;
  el.style.position = el.style.position || "relative";
  const pos = getElementPosition(el);
  pos.x += dx;
  pos.y += dy;
  positions.set(el, pos);
  el.style.transform = `translate(${pos.x}px, ${pos.y}px)`;
  onChanged?.();
}

export function resetElementPosition(el: HTMLElement, onChanged?: () => void): void {
  if (!el) return;
  positions.set(el, { x: 0, y: 0 });
  el.style.transform = "";
  onChanged?.();
}

export function markElementDeleted(
  el: HTMLElement,
  deleted: boolean,
  onChanged?: () => void
): void {
  if (!el) return;
  if (deleted) {
    el.setAttribute("data-user-deleted", "true");
    el.style.display = "none";
  } else {
    el.removeAttribute("data-user-deleted");
    el.style.display = "";
  }
  onChanged?.();
}

export function getDeletedElements(container: HTMLElement): HTMLElement[] {
  if (!container) return [];
  return Array.from(
    container.querySelectorAll<HTMLElement>("[data-user-deleted='true']")
  );
}

/**
 * Enable drag-and-drop on the layout blocks within `container`.
 * Returns a cleanup function that fully disables the interactables.
 */
export function enableTemplateDrag(
  container: HTMLElement,
  onChanged?: () => void
): () => void {
  if (!container) return () => {};

  const targets = collectTargets(container);

  targets.forEach((el) => {
    el.style.position = el.style.position || "relative";
    el.style.cursor = "grab";
    el.style.touchAction = "none";
    el.classList.add("template-draggable-block");

    const pos = getElementPosition(el);
    positions.set(el, pos);

    const it = interact(el).draggable({
      inertia: false,
      autoScroll: true,
      listeners: {
        start() {
          el.classList.add("template-dragging");
        },
        move(event) {
          const p = positions.get(el) ?? { x: 0, y: 0 };
          p.x += event.dx;
          p.y += event.dy;
          positions.set(el, p);
          el.style.transform = `translate(${p.x}px, ${p.y}px)`;
          el.classList.add("template-dragging");
        },
        end() {
          el.classList.remove("template-dragging");
          onChanged?.();
        },
      },
    });
    interactables.set(el, it);
  });

  return () => {
    targets.forEach((el) => {
      const it = interactables.get(el);
      if (it) it.unset();
      interactables.delete(el);
      positions.delete(el);
      // Only clear the dragging class; keep the transform so edits persist on reload.
      el.classList.remove("template-dragging");
      el.classList.remove("template-draggable-block");
      el.style.cursor = "";
    });
  };
}

/** Convenience: disable drag for all interactables currently registered on `container`. */
export function disableTemplateDrag(container: HTMLElement): void {
  if (!container) return;
  const targets = collectTargets(container);
  targets.forEach((el) => {
    const it = interactables.get(el);
    if (it) it.unset();
    interactables.delete(el);
    positions.delete(el);
    el.classList.remove("template-dragging");
    el.classList.remove("template-draggable-block");
    el.style.cursor = "";
  });
}